import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { DisplayDateTime } from "src/base-components/Date";
import { Divider } from "src/base-components/Divider";
import { UserPill } from "src/cases/common/UserPill";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";

type HistoryEntryFrameProps = {
  isLast?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
  onToggle?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  dataLoc?: string;
};

export const HistoryEntryFrame: React.FC<HistoryEntryFrameProps> = ({
  isLast = false,
  icon,
  children,
  onToggle,
  isExpanded,
  dataLoc = "history-item",
}) => {
  "use memo";

  const dataLocDivider = `history-item-${dataLoc}-divider`;

  return (
    <div
      className="flex items-stretch gap-x-2.5"
      data-loc={`history-item-${dataLoc}`}
    >
      <div className="flex flex-col items-center gap-y-1">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gray-200">
          {icon}
        </div>
        {!isLast && <Divider orientation="vertical" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-y-1">
        <div className="flex w-full flex-col gap-y-1">{children}</div>

        {onToggle ? (
          <ExpandableDivider
            dataLoc={dataLocDivider}
            isExpanded={isExpanded}
            onToggle={onToggle}
          />
        ) : (
          !isLast && (
            <div className="py-2" data-loc={dataLocDivider}>
              <Divider />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export type HistoryGroupItem = {
  timestamp: string;
  headline: React.ReactNode;
  sideAction?: React.ReactNode;
  footerAction?: React.ReactNode;
  content?: React.ReactNode;
  actorId?: string | null;
  eventType: string;
};

type HistoryGroupProps = {
  eventType: string;
  isLast?: boolean;
  icon: IconProp;
  items: HistoryGroupItem[];
  onToggle?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
};

// Syntaxic sugar for a single item history group
type HistorySingleItemProps = Omit<HistoryGroupProps, "items" | "dataLoc"> &
  HistoryGroupProps["items"][number] & {
    onToggle?: (isExpanded: boolean) => void;
    isExpanded?: boolean;
    sideAction?: React.ReactNode;
    footerAction?: React.ReactNode;
  };

export const HistoryEntryGroup: React.FC<HistoryGroupProps> = ({
  isLast = false,
  icon,
  items,
  onToggle,
  isExpanded,
  eventType,
}) => {
  "use memo";

  return (
    <HistoryEntryFrame
      dataLoc={`${eventType}-group`}
      icon={<Icon icon={icon} size="2xs" />}
      isExpanded={isExpanded}
      isLast={isLast}
      onToggle={onToggle}
    >
      {items.map(
        (item: HistoryGroupItem, index: number, array: HistoryGroupItem[]) => (
          <div key={item.timestamp} className="flex flex-col gap-y-1">
            <HistoryItemFrame
              content={item.content}
              dataLoc={item.eventType}
              footer={
                <HistoryEntryFooter
                  action={item.footerAction}
                  actorId={item.actorId}
                  timestamp={item.timestamp}
                />
              }
              headline={item.headline}
              sideAction={item.sideAction}
            />
            {index < array.length - 1 && (
              <div className="py-2">
                <Divider />
              </div>
            )}
          </div>
        ),
      )}
    </HistoryEntryFrame>
  );
};

const HistoryEntryFooter: React.FC<{
  timestamp: string;
  actorId?: string | null;
  action?: React.ReactNode;
}> = ({ timestamp, actorId, action }) => {
  return (
    <div className="flex items-center gap-x-1">
      {actorId && <UserPill userId={actorId} />}
      {actorId && " • "}
      <DisplayDateTime value={timestamp} />
      {action}
    </div>
  );
};

export const HistorySingleEntry: React.FC<HistorySingleItemProps> = ({
  isLast = false,
  icon,
  timestamp,
  headline,
  content,
  actorId,
  onToggle,
  isExpanded,
  sideAction,
  footerAction,
  eventType,
}) => {
  "use memo";

  return (
    <HistoryEntryFrame
      dataLoc={`${eventType}-single`}
      icon={<Icon icon={icon} size="2xs" />}
      isExpanded={isExpanded}
      isLast={isLast}
      onToggle={onToggle}
    >
      <HistoryItemFrame
        content={content}
        dataLoc={eventType}
        footer={
          <HistoryEntryFooter
            action={footerAction}
            actorId={actorId}
            timestamp={timestamp}
          />
        }
        headline={headline}
        isLast={isLast}
        sideAction={sideAction}
      />
    </HistoryEntryFrame>
  );
};

type HistoryItemProps = {
  dataLoc: string;
  isLast?: boolean;
  footer: React.ReactNode;
  headline: React.ReactNode;
  content?: React.ReactNode;
  sideAction?: React.ReactNode;
  onToggle?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
};

const HistoryItemFrame: React.FC<HistoryItemProps> = ({
  dataLoc,
  footer,
  headline,
  content,
  sideAction,
}) => {
  "use memo";

  return (
    <div
      className="flex flex-col gap-y-1.5"
      data-loc={`history-item-${dataLoc}`}
    >
      <div className="flex items-start gap-x-2">
        <div className="w-full font-inter-medium-12px text-gray-800">
          {headline}
        </div>
        {sideAction && <div>{sideAction}</div>}
      </div>
      {content}
      <div className="font-inter-medium-11px text-gray-500">{footer}</div>
    </div>
  );
};

export const HistoryItemLoading: React.FC<Pick<HistoryItemProps, "isLast">> = ({
  isLast = false,
}) => {
  "use memo";

  return (
    <HistoryEntryFrame
      icon={<Skeleton height="h-3.5" width="w-3.5" />}
      isLast={isLast}
    >
      <HistoryItemFrame
        content={
          <div className="flex flex-col gap-y-1">
            <Skeleton height="h-4" width="w-4/5" />
            <Skeleton height="h-4" width="w-3/5" />
          </div>
        }
        dataLoc="loading"
        footer={<Skeleton height="h-3" width="w-1/3" />}
        headline={<Skeleton height="h-4" width="w-2/3" />}
        isLast={isLast}
      />
    </HistoryEntryFrame>
  );
};

export const CommentBox: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="rounded-lg bg-gray-50 p-2">{children}</div>;

const ExpandableDivider: React.FC<{
  dataLoc?: string;
  isExpanded?: boolean;
  onToggle: (isExpanded: boolean) => void;
}> = ({ dataLoc, isExpanded = false, onToggle }) => {
  "use memo";

  return (
    <div
      className="flex cursor-pointer items-center gap-x-2"
      data-loc={`${dataLoc}-expand`}
      onClick={() => onToggle(!isExpanded)}
    >
      <Divider />
      <div
        className={twJoin(
          "transition-transform duration-300",
          isExpanded && "rotate-180",
        )}
      >
        <Icon variant="disabled" icon={faChevronDown} size="xs" />
      </div>
      <Divider />
    </div>
  );
};
